/**
 * emailSanitize.ts
 *
 * Removes invisible/control characters that break sending or identity from recipient email/name.
 * (OMD-204) Shared single source used across send paths (getMailData / scheduled EML / createEmailParam)
 * and search input / display.
 *
 * Stripped codepoint ranges (visible chars and normal Unicode are preserved):
 * - U+0000-001F  C0 control
 * - U+007F-009F  DEL + C1 control
 * - U+00AD       soft hyphen
 * - U+061C       Arabic letter mark
 * - U+180E       Mongolian vowel separator
 * - U+200B-200F  zero-width & bidi marks (ZWSP/ZWNJ/ZWJ/LRM/RLM)
 * - U+2028-2029  line/paragraph separator
 * - U+202A-202E  bidi embeddings/overrides
 * - U+2060       word joiner
 * - U+2066-2069  bidi isolates
 * - U+FE00-FE0F  variation selectors
 * - U+FEFF       BOM / ZWNBSP
 *
 * Note: ASCII hyphen (-), space, `@`, `.` are NOT stripped (normal addresses preserved).
 * Visible-but-invalid chars (inner space, duplicate `@`, ...) are left to existing validation / server reject.
 *
 * Ranges are declared as numeric codepoints (ASCII-only source) and compiled to a RegExp so that no
 * literal invisible character ever appears in this file (some, e.g. U+2028/2029, are JS line terminators).
 */
const INVISIBLE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0000, 0x001f],
  [0x007f, 0x009f],
  [0x00ad, 0x00ad],
  [0x061c, 0x061c],
  [0x180e, 0x180e],
  [0x200b, 0x200f],
  [0x2028, 0x2029],
  [0x202a, 0x202e],
  [0x2060, 0x2060],
  [0x2066, 0x2069],
  [0xfe00, 0xfe0f],
  [0xfeff, 0xfeff],
]

const toClass = ([start, end]: readonly [number, number]): string => {
  const esc = (cp: number) => `\\u${cp.toString(16).padStart(4, '0')}`
  return start === end ? esc(start) : `${esc(start)}-${esc(end)}`
}

const INVISIBLE = new RegExp(`[${INVISIBLE_RANGES.map(toClass).join('')}]`, 'g')

export const stripInvisible = (s?: string | null): string => (s ?? '').replace(INVISIBLE, '')

// email: strip invisible/control chars + trim ends. Case and inner visible chars are left unchanged.
export const sanitizeEmail = (email?: string | null): string => stripInvisible(email).trim()
